import { MultiLineChart } from '@design/charts/multi-line-chart/MultiLineChart';
import {
  MultiLineChartLine,
  TooltipSort,
} from '@design/charts/multi-line-chart/MultiLineChart.types';
import { useChartTheme } from '@terminal/views/turbo-chart/hooks/use-chart-theme/useChartTheme';
import * as d3 from 'd3';
import { useTranslation } from 'react-i18next';

const getYAxisFormatter = (
  yScale: d3.ScaleLinear<number, number>,
  tickCount: number
) => {
  const ticks = yScale.ticks(tickCount);
  const step = Math.abs((ticks[1] ?? 0) - (ticks[0] ?? 0));
  // Only the decimals needed to tell neighbouring ticks apart, so a whole-number
  // step reads '1%' rather than '1.00%'.
  const precision = step > 0 ? Math.max(0, -Math.floor(Math.log10(step))) : 0;
  const format = d3.format(`.${precision}f`);

  return (value: number) => `${format(value)}%`;
};

export interface AnalyticsLineChartProps {
  multiLineConfig: Record<string, MultiLineChartLine[]>;
  showBreakdown?: boolean;
  showBenchmark?: boolean;
  tooltipSort?: TooltipSort;
  fromDate?: Date;
  toDate?: Date;
  includeTime?: boolean;
}

export const AnalyticsLineChart = ({
  multiLineConfig,
  showBreakdown,
  showBenchmark,
  tooltipSort = 'desc',
  fromDate,
  toDate,
  includeTime,
}: AnalyticsLineChartProps) => {
  const { t, i18n } = useTranslation(['common']);
  const { colors } = useChartTheme(['soft']);

  const getLines = () => {
    const benchmark = showBenchmark ? multiLineConfig.benchmark : [];
    const lines = showBreakdown
      ? multiLineConfig.breakdown
      : multiLineConfig.aggregate;

    return [...benchmark, ...lines];
  };

  const lines = getLines();
  const withBands = lines.length === 1;
  return (
    <>
      <MultiLineChart
        hasTooltip
        hasLegend={showBreakdown}
        lines={lines}
        yAxisFormatType={getYAxisFormatter}
        tooltipFormatType="percent2f"
        tooltipSort={tooltipSort}
        thresholdData={
          withBands ? multiLineConfig.aggregate[0].data : undefined
        }
        fillColors={{
          positive: colors.threshold.positive.value,
          negative: colors.threshold.negative.value,
        }}
        notAvailableLabel={t('common:table.na')}
        locale={i18n.resolvedLanguage}
        showBreakdown={showBreakdown}
        fromDate={fromDate}
        toDate={toDate}
        includeTime={includeTime}
      />
    </>
  );
};
